(ns manage.adset-decision-tree-page.event
  (:require ["@xyflow/react" :refer [applyEdgeChanges applyNodeChanges]]
            [cljs-bean.core :refer [->clj ->js]]
            [clojure.set :as set]
            [clojure.string :as str]
            [common.db.effects.util :as effects.util]
            [common.url-template :as url-tpl]
            [common.util :as util]
            [linked.core :as linked]
            [manage.adset-decision-tree-page.editor.util :refer [assoc-reactflow-object assoc-reactflow-objects-for-ids]]
            [manage.adset-decision-tree-page.layout :as layout]
            [manage.adset-decision-tree-page.rotation :as rotation]
            [manage.adset-decision-tree-page.selection :as selection]
            [manage.adset-decision-tree-page.view.node-conversion
             :as node-conversion]
            [manage.adset-decision-tree-page.view.predicate-editor.converter :as predicate-converter]
            [mate.core :as mc]
            [mate.re-frame :as mr]
            [medley.core :as medley]
            [re-frame.core :as rf]))

(defn generic-error-handler [{:keys [status] :as error}]
  (rf/dispatch [::register-communication-error error])
  (if (= status 401)
    (rf/dispatch [:modal/open
                  {:id :decision-tree/logged-out-error-modal}])
    (rf/dispatch [:modal/open
                  {:id :decision-tree/save-error-modal}])))

(defn track-mutation [effects http-fx-config]
  (let [mutation-ids (-> effects :db :decision-tree-editor :tracked-mutation-ids)
        ;; Reuse the first id number which is not currently used.
        new-mutation-id (-> (range)
                            (->> (remove mutation-ids))
                            (first))
        ;; Stop tracking the mutation once the http call is responded, then forward to the original callback.
        wrapped-http-fx-config (-> http-fx-config
                                   (assoc :on-success (fn [response]
                                                        (rf/dispatch [::stop-tracking-mutation new-mutation-id])
                                                        (effects.util/invoke-callback (:on-success http-fx-config) response))
                                          :on-failure (fn [response]
                                                        (rf/dispatch [::stop-tracking-mutation new-mutation-id])

                                                        (effects.util/invoke-callback (:on-failure http-fx-config) response)
                                                        (generic-error-handler response))))]
    (-> effects
        (mr/update-db update-in [:decision-tree-editor] dissoc :communication-error)
        (mr/update-db update-in [:decision-tree-editor :tracked-mutation-ids] conj new-mutation-id)
        (mr/conj-fx [:http wrapped-http-fx-config]))))

(rf/reg-event-db ::register-communication-error
  [(rf/path :decision-tree-editor)]
  (fn [editor [_ error]] (assoc editor :communication-error error)))

;; Works for both the Reactflow nodes and the Reactflow edges.
;; Avoids modifying the js-object if no modification is needed.
(defn- set-reactflow-object-selected [^js js-object should-be-selected]
  (assoc-reactflow-object js-object :selected should-be-selected))

(defn- sync-reactflow-with-selection [editor things-to-deselect things-to-select]
  (let [update-node-selected-attributes
        (fn [reactflow-nodes]
          (-> reactflow-nodes
              (as-> xxx
                (reduce (fn [acc {:keys [type decision-node-id]}]
                          (cond-> acc
                            (and (= type :selection/decision-node)
                                 (contains? acc decision-node-id))
                            (update decision-node-id set-reactflow-object-selected false)))
                        xxx
                        things-to-deselect))
              (as-> xxx
                (reduce (fn [acc {:keys [type decision-node-id]}]
                          (cond-> acc
                            (and (= type :selection/decision-node)
                                 (contains? acc decision-node-id))
                            (update decision-node-id set-reactflow-object-selected true)))
                        xxx
                        things-to-select))))]
    (-> editor
        (update :reactflow-nodes update-node-selected-attributes)
        ;; To do later
        #_(update :reactflow-edges))))

(defn- update-selection [editor things-to-deselect things-to-select]
  (-> editor
      (update :selection
              (fn [current-selection]
                (-> current-selection
                    (cond->>
                      (some? things-to-deselect)
                      (remove (comp (into #{} (map selection/get-canonical-selected-item) things-to-deselect)
                                    selection/get-canonical-selected-item)))
                    (concat things-to-select)
                    vec)))
      (sync-reactflow-with-selection things-to-deselect things-to-select)
      (assoc-in [:ad-variant-search :enabled] false)
      (cond-> (seq things-to-deselect) (assoc :new-node-selected false))))

;; This replaces the current selection with a new one, while maintaining the order
;; of the items remaining selected.
(defn- set-selection [{:keys [selection] :as editor} new-selection]
  (let [current-selection-set (set selection)
        new-selection-set (set new-selection)
        ;; Find out what things should be deselected, and what things should be selected.
        things-to-deselect (set/difference current-selection-set new-selection-set)
        things-to-select (set/difference new-selection-set current-selection-set)]
    (update-selection editor things-to-deselect things-to-select)))

(defn set-selected-nodes [{:keys [node-id->node-data] :as editor} node-ids]
  (let [new-selection (keep (fn [node-id]
                              (some-> node-id->node-data
                                      (get node-id)
                                      selection/get-selected-item-for-node))
                            node-ids)]
    (set-selection editor new-selection)))

(defn handle-initial-selection
  [{:keys [node-id->be-node selection] :as editor}
   {:keys [immutable-node-id default-outcome]}]
  (if (seq selection) ; Do not overwrite an existing selection
    editor
    (let [initial-node-id-to-select (some (fn [{:keys [id immutable-id]}]
                                            (when (= immutable-id immutable-node-id) id))
                                          (vals node-id->be-node))]
      (if default-outcome
        (set-selection editor [{:decision-node-id initial-node-id-to-select
                                :type :selection/default-outcome-node}])
        (set-selected-nodes editor [initial-node-id-to-select])))))

(def default-state
  {:node-id->be-node {}
   :node-id->node-data {}
   :reactflow-nodes (linked/map) ; {node-id  reactflow-node}
   :reactflow-edges (linked/map) ; {edge-id  reactflow-edge} edge-id has format "parent-node-id->node-id"
   :new-node-selected false
   :tracked-mutation-ids #{}})

(defn refresh-editor [{loaded-tree-read-only :read-only
                       loaded-decision-tree-id :decision-tree-id
                       :as loaded-editor}
                      {{{be-nodes :nodes} :data} :be-response
                       :keys [decision-tree-id read-only]}
                      {:keys [immutable-node-id default-outcome]}]
  (cond-> (if (and (= loaded-decision-tree-id decision-tree-id)
                   (= loaded-tree-read-only read-only))
            loaded-editor
            (assoc default-state
                   :decision-tree-id decision-tree-id
                   :root-node-id (->> be-nodes
                                      (some (fn [{:keys [type id]}]
                                              (when (node-conversion/root-node-type? type)
                                                id))))
                   :read-only read-only))
    true (node-conversion/update-fe-nodes-from-be-nodes be-nodes)
    (some? immutable-node-id) (handle-initial-selection {:immutable-node-id immutable-node-id
                                                         :default-outcome default-outcome})))

(defn maybe-schedule-re-layout [effects]
  (let [auto-layout (get-in effects [:db :decision-tree-editor :auto-layout])]
    (cond-> effects
      ;; Re-layout when all nodes are saved
      auto-layout (mr/conj-fx [:dispatch [::schedule-after-nodes-are-saved
                                          #(rf/dispatch [::re-layout-nodes])]]))))

(rf/reg-event-fx ::refresh-editor
  (fn [{:keys [db]} [_ args]]
    (let [query-params (get-in db [:current-route :query-params])]
      (-> {:db db}
          (mr/update-db update :decision-tree-editor refresh-editor args query-params)))))

(rf/reg-event-db ::cleanup-editor
  (fn [db]
    (dissoc db :decision-tree-editor)))

(defn be-patch-nodes [effects node-ids delay-ms]
  (cond-> effects
    (seq node-ids)
    (-> (mr/update-db update-in [:decision-tree-editor :nodes-to-patch] (fnil into #{}) node-ids)
        (mr/conj-fx [:dispatch-debounced {:id ::be-patch-pending-nodes
                                          :event [::be-patch-pending-nodes]
                                          :delay-ms delay-ms}]))))

(rf/reg-event-fx ::resolve-delete-confirm
  (fn [_ [_ resolve]]
    {:fx [[:promise/resolve resolve]]}))

(defn- get-path-node-ids
  [reactflow-edges edge-id]
  (let [source->edge (group-by
                      (comp parse-long :source ->clj)
                      (vals reactflow-edges))
        get-node-id (fn [edge-id]
                      (let [{:keys [target]} (->clj (get reactflow-edges edge-id))]
                        (parse-long target)))
        get-children (fn [node-id]
                       (map
                        (comp get-node-id :id ->clj)
                        (get source->edge node-id)))]
    (tree-seq (constantly true) get-children (get-node-id edge-id))))

(defn- get-path-edges-for-nodes
  [reactflow-edges node-ids]
  (let [node-id-set (set node-ids)]
    (->> (vals reactflow-edges)
         (keep (fn [^js edge]
                 (let [{:keys [id target]} (->clj edge)]
                   (when (contains? node-id-set (parse-long target))
                     id)))))))

(defn update-sub-path-visibility
  [{:keys [reactflow-edges] :as editor} edge-id is-visible]
  (let [node-ids (get-path-node-ids reactflow-edges edge-id)
        descendant-edge-ids (get-path-edges-for-nodes reactflow-edges node-ids)
        ;; Include the initial edge plus all descendant edges
        edge-ids (cons edge-id descendant-edge-ids)]
    (-> editor
        (update :reactflow-nodes
                assoc-reactflow-objects-for-ids
                node-ids
                :hidden (not is-visible))
        (update :reactflow-edges
                assoc-reactflow-objects-for-ids
                edge-ids
                :hidden (not is-visible)))))

(defn show-all-sub-path-for-nodes
  [{:keys [reactflow-edges] :as editor} node-ids]
  (->> (get-path-edges-for-nodes reactflow-edges node-ids)
       (reduce (fn [editor edge-id]
                 (update-sub-path-visibility editor edge-id true))
               editor)))

(rf/reg-event-fx ::delete-nodes
  [(rf/inject-cofx :sub :url-context)
   (rf/inject-cofx :sub :resource.user/token)]
  (fn [{:keys [db url-context resource.user/token]} [_ {:keys [node-ids] :as params}]]
    (-> {:db db}

        (track-mutation
         {:method :post
          :uri (url-tpl/render :api/content-build-adset-decision-tree-bulk-remove
                               (assoc url-context :decision-tree-id
                                      (get-in db [:decision-tree-editor :decision-tree-id])))
          :headers {"Authorization" token}
          :params (map (fn [id] {:id id}) node-ids)
          :on-success [::be-delete-nodes-on-success params]
          :on-failure [::be-delete-nodes-on-failure]}))))

(rf/reg-event-fx ::be-delete-nodes-on-success
  (fn [{:keys [db]} [_ {:keys [node-ids clear-selection]
                        :or {clear-selection true}}]]
    (let [node-id->node-data (-> db :decision-tree-editor :node-id->node-data)
          decision-node-id->outcome-node-ids (->> (vals node-id->node-data)
                                                  (filter node-conversion/outcome-node?)
                                                  (mc/group-by :parent-id :id))
          extended-node-ids (-> (set node-ids)
                                (into (mapcat decision-node-id->outcome-node-ids) node-ids))]
      (-> {:db db}
          (mr/update-db update :decision-tree-editor
                        #(-> %
                             (cond-> clear-selection (set-selection nil))
                             (show-all-sub-path-for-nodes node-ids)
                             (update :node-id->be-node util/dissoc-many extended-node-ids)
                             (update :node-id->node-data util/dissoc-many extended-node-ids)
                             (update :node-id->node-data update-vals
                                     (fn [node-data]
                                       (update node-data :outcome-node-ids
                                               (fn [outcome-node-ids]
                                                 (->> outcome-node-ids
                                                      (into [] (remove extended-node-ids)))))))
                             (update :reactflow-nodes util/dissoc-many extended-node-ids)
                             node-conversion/rebuild-reactflow-edges))))))

;; Handled by the generic error handler in track-mutation
(rf/reg-event-db ::be-delete-nodes-on-failure
  identity)

(rf/reg-event-fx ::on-nodes-change
  (fn [{:keys [db]} [_ ^js changes]]
    (let [{removal-changes "remove"
           selection-changes "select"
           positional-changes "position"
           :as grouped-changes} (group-by :type (->clj changes))
          non-removal-non-select-changes (mapcat val (dissoc grouped-changes "remove" "select"))
          {things-to-deselect false
           things-to-select true} (->> selection-changes
                                       (mc/group-by :selected
                                                    (fn [selection-change]
                                                      {:type :selection/decision-node
                                                       :decision-node-id (-> selection-change :id parse-long)})
                                                    conj #{}))

          ;; If a node becomes selected, make sure that any selected outcome node becomes deselected.
          current-selection (get-in db [:decision-tree-editor :selection])
          things-to-deselect (cond-> (or things-to-deselect #{})
                               (seq things-to-select)
                               (into (->> current-selection
                                          (remove (fn [selected-item]
                                                    (= (:type selected-item) :selection/decision-node))))))]

      (cond-> {:db db}

        ;; Apply the changes on the FE data without the "remove" or "select" changes
        true
        (mr/update-db update-in [:decision-tree-editor :reactflow-nodes]
                      (fn [reactflow-nodes]
                        (->> (to-array (vals reactflow-nodes))
                             (applyNodeChanges (->js non-removal-non-select-changes))
                             (into (linked/map)
                                   (map (fn [^js node]
                                          [(parse-long (.-id node)) node]))))))

        true
        (mr/update-db update :decision-tree-editor
                      update-selection things-to-deselect things-to-select)

        ;; In the BE, patch the nodes whose position changed, with a 1000ms debounce
        (seq positional-changes)
        (be-patch-nodes (mapv (comp parse-long :id) positional-changes) 1000)

        (seq removal-changes)
        (mr/conj-fx
         [:dispatch [::delete-nodes
                     {:node-ids (mapv (comp parse-long :id) removal-changes)}]])))))

(defn unparent-nodes [effects node-ids-to-unparent]
  (mr/update-db effects update-in [:decision-tree-editor :node-id->node-data]
                (fn [node-id->node-data]
                  (reduce (fn [acc node-id]
                            (assoc-in acc [node-id :parent-id] nil))
                          node-id->node-data
                          node-ids-to-unparent))))

(rf/reg-event-fx ::on-edges-change
  (fn [{:keys [db]} [_ ^js changes]]
    (let [node-ids-to-unparent (keep (fn [change]
                                       (when (= (:type change) "remove")
                                         (let [[_parent-node-id-str node-id-str] (str/split (:id change) #"->")]
                                           (parse-long node-id-str))))
                                     (->clj changes))]
      (-> {:db db}
          (unparent-nodes node-ids-to-unparent)

          ;; FIXME: Ideally, the removals in reactflow-edges should be
          ;; derived from `(dissoc ,,, :parent-id)` above, in node-id->node-data
          (mr/update-db update-in [:decision-tree-editor :reactflow-edges]
                        (fn [reactflow-edges]
                          (let [new-edges (->> (to-array (vals reactflow-edges))
                                               (applyEdgeChanges changes))]
                            (into (linked/map)
                                  (map (fn [^js edge] [(.-id edge) edge]))
                                  new-edges))))

          ;; Edge removal is not an operation which requires debouncing, so delay-ms is 0.
          (be-patch-nodes node-ids-to-unparent 0)))))

(rf/reg-event-fx ::on-connect
  (fn [{:keys [db]} [_ ^js params]]
    (let [root-node-id (-> db :decision-tree-editor :root-node-id)
          node-id->node-data (-> db :decision-tree-editor :node-id->node-data)
          {new-edge-source :source
           new-edge-target :target
           new-edge-source-handle :sourceHandle} (->clj params)
          new-edge-source-node-id (parse-long (or new-edge-source-handle new-edge-source))
          new-edge-target-node-id (parse-long new-edge-target)
          rotation-parent (rotation/rotation-parent-for-outcome new-edge-source-node-id
                                                                node-id->node-data)
          connection-blocked? (and rotation-parent
                                   (rotation/has-path-ad-variants? rotation-parent
                                                                   node-id->node-data))
          ;; The root node can connect to many children at once (e.g. many parallel
          ;; decision nodes, or the weighted rotation node alongside others), so its
          ;; existing children should never be unparented when a new one connects.
          allow-multiple-children? (= new-edge-source-node-id root-node-id)
          [node-id-to-unparent] (when-not allow-multiple-children?
                                  (->> (vals node-id->node-data)
                                       (keep (fn [{:keys [parent-id id]}]
                                               (when (= parent-id new-edge-source-node-id)
                                                 id)))))]
      (if connection-blocked?
        {:db db}
        (-> {:db db}
            (maybe-schedule-re-layout)
            (cond-> (some? node-id-to-unparent) (unparent-nodes [node-id-to-unparent]))
            (mr/update-db assoc-in [:decision-tree-editor :node-id->node-data
                                    new-edge-target-node-id :parent-id]
                          new-edge-source-node-id)
            (mr/update-db update :decision-tree-editor node-conversion/rebuild-reactflow-edges)
            (be-patch-nodes [new-edge-target-node-id] 0)
            (cond-> (some? node-id-to-unparent)
              (mr/update-db assoc-in [:decision-tree-editor :node-id->be-node
                                      node-id-to-unparent :parent-id]
                            nil)))))))

(defn- compute-patch-data [new-be-node old-be-node]
  (into {}
        (filter (fn [[k v]]
                  (not= v (get old-be-node k))))
        new-be-node))

(rf/reg-event-db ::stop-tracking-mutation
  (fn [db [_ mutation-id]]
    (update-in db [:decision-tree-editor :tracked-mutation-ids] disj mutation-id)))

(defn- run-once-all-nodes-are-saved [effects]
  (let [{:keys [nodes-to-patch
                nodes-currently-being-patched
                all-nodes-saved-callbacks]} (-> effects :db :decision-tree-editor)]
    (cond-> effects
      ;; Are there no new nodes to patch and no ongoing node patching?
      (and (empty? nodes-to-patch)
           (empty? nodes-currently-being-patched))
      ;; If yes, call all the callbacks in the same order they went in the queue.
      (-> (mr/into-fx (for [callback all-nodes-saved-callbacks]
                        [:promise/resolve callback]))
          (mr/update-db update :decision-tree-editor dissoc :all-nodes-saved-callbacks)))))

(rf/reg-event-fx ::schedule-after-nodes-are-saved
  (fn [{:keys [db]} [_ callback]]
    (let [{:keys [nodes-to-patch]} (-> db :decision-tree-editor)]
      (if (empty? nodes-to-patch)
        {:fx [[:promise/resolve callback]]}
        {:db (-> db
                 (update-in [:decision-tree-editor :all-nodes-saved-callbacks] (fnil conj []) callback))}))))

(defn- should-sync-root-weighted-distribution? [db node-id patch-data]
  (let [node-id->node-data (get-in db [:decision-tree-editor :node-id->node-data])
        root-node-id (get-in db [:decision-tree-editor :root-node-id])
        rotation-node (rotation/find-root-weighted-rotation-node root-node-id node-id->node-data)]
    (and rotation-node
         (rotation/weighted-rotation-path-outcome? node-id node-id->node-data)
         (or (contains? patch-data :ad-variants)
             (get-in patch-data [:config :rotation :weight])))))

(rf/reg-event-fx ::be-patch-node
  [(rf/inject-cofx :sub :url-context)
   (rf/inject-cofx :sub :resource.user/token)]
  (fn [{:keys [db url-context resource.user/token]} [_ node-id patch-data on-success]]
    (let [decision-tree-id (get-in db [:decision-tree-editor :decision-tree-id])]
      (when (seq patch-data)
        (-> {:db db}
            (mr/update-db update-in [:decision-tree-editor :nodes-currently-being-patched]
                          (fnil into #{}) [node-id])
            (track-mutation
             {:method :patch
              :uri (url-tpl/render :api/content-build-adset-decision-tree-node
                                   (-> url-context
                                       (assoc :decision-tree-id decision-tree-id
                                              :node-id node-id)))
              :headers {"Authorization" token}
              :params patch-data
              :on-success [::be-patch-node-on-success node-id patch-data on-success]
              :on-failure [::be-patch-node-on-failure]}))))))

(rf/reg-event-fx ::be-patch-node-on-success
  (fn [{:keys [db]} [_ node-id patch-data on-success _response]]
    (let [updated-db (-> db
                         (update-in [:decision-tree-editor :node-id->be-node node-id]
                                    merge patch-data)
                         (update-in [:decision-tree-editor :node-id->node-data node-id]
                                    merge patch-data)
                         (update-in [:decision-tree-editor :nodes-currently-being-patched]
                                    disj node-id))
          sync? (should-sync-root-weighted-distribution? updated-db node-id patch-data)]
      (-> {:db updated-db}
          (mr/conj-fx [:promise/resolve #(effects.util/invoke-callback on-success)])
          (cond-> sync? (mr/conj-fx [:dispatch [::sync-root-weighted-distribution]]))))))

;; Handled by the generic error handler in track-mutation
(rf/reg-event-db ::be-patch-node-on-failure
  identity)

(rf/reg-event-fx ::be-patch-pending-nodes
  [(rf/inject-cofx :sub :url-context)
   (rf/inject-cofx :sub :resource.user/token)]
  (fn [{:keys [db url-context resource.user/token]} _]
    (when-some [node-ids (get-in db [:decision-tree-editor :nodes-to-patch])]
      (let [{:keys [node-id->be-node node-id->node-data reactflow-nodes]} (:decision-tree-editor db)
            decision-tree-id (get-in db [:decision-tree-editor :decision-tree-id])
            mutations (for [node-id node-ids
                            :when (contains? node-id->node-data node-id)
                            :let [old-be-node (-> node-id node-id->be-node)
                                  new-be-node (node-conversion/fe-node->be-node (-> node-id reactflow-nodes ->clj)
                                                                                (-> node-id node-id->node-data))
                                  patch-data (compute-patch-data new-be-node old-be-node)]
                            :when (seq patch-data)]
                        {:node-id node-id
                         :old-be-node old-be-node
                         :new-be-node new-be-node
                         :patch-data patch-data})
            mutation-ids (map :node-id mutations)]
        (-> {:db db}
            (mr/update-db update :decision-tree-editor dissoc :nodes-to-patch)
            (mr/update-db update-in [:decision-tree-editor :nodes-currently-being-patched] (fnil into #{}) mutation-ids)
            (as-> effects
              (reduce track-mutation effects
                      (for [{:keys [node-id patch-data]} mutations]
                        {:method :patch
                         :uri (url-tpl/render :api/content-build-adset-decision-tree-node
                                              (-> url-context
                                                  (assoc :decision-tree-id decision-tree-id
                                                         :node-id node-id)))
                         :headers {"Authorization" token}
                         :params patch-data
                         :on-success [::be-patch-pending-node-on-success node-id]
                         :on-failure [::be-patch-pending-node-on-failure node-id]}))))))))

(rf/reg-event-fx ::be-patch-pending-node-on-success
  (fn [{:keys [db]} [_ node-id response]]
    (-> {:db db}
        (mr/update-db assoc-in [:decision-tree-editor :node-id->be-node node-id]
                      (node-conversion/qualify-be-node-type (:data response)))
        (mr/update-db update-in [:decision-tree-editor :nodes-currently-being-patched]
                      disj node-id)
        (run-once-all-nodes-are-saved))))

(rf/reg-event-fx ::be-patch-pending-node-on-failure
  (fn [{:keys [db]} [_ node-id _response]]
    (let [old-be-node (get-in db [:decision-tree-editor :node-id->be-node node-id])]
      {:db (update db :decision-tree-editor
                   (fn [editor]
                     (-> editor
                         (update :nodes-currently-being-patched disj node-id)
                         ;; Revert FE state to latest known be state if there was a failure
                         (node-conversion/update-fe-nodes-from-be-nodes [old-be-node]))))})
    ;; (run-once-all-nodes-are-saved)
    ;; Q: Should it run also when there was a save failure? Probably not:
    ;; Example:
    ;; - user pressed publish while saving assigned variant,
    ;; - assignment save fails, still publishing will be ran
    ;; - user will have a published tree without the assigned variant
    ))

(rf/reg-event-fx ::be-create-node
  [(rf/inject-cofx :sub :url-context)
   (rf/inject-cofx :sub :resource.user/token)]
  (fn [{:keys [db url-context resource.user/token]} [_ be-node-initial-data]]
    (let [decision-tree-id (get-in db [:decision-tree-editor :decision-tree-id])
          params (cond-> be-node-initial-data
                   (:type be-node-initial-data)
                   (update :type node-conversion/api-node-type))]
      (-> {:db db}
          (track-mutation {:method :post
                           :uri (url-tpl/render :api/content-build-adset-decision-tree-nodes
                                                (-> url-context
                                                    (assoc :decision-tree-id decision-tree-id)))
                           :headers {"Authorization" token}
                           :params params
                           :on-success [::be-create-node-on-success]
                           :on-failure [::be-create-node-on-failure]})))))

(defn get-outcome-selection-data [{:keys [id parent-id]}]
  {:type :selection/outcome-node
   :outcome-node-id id
   :decision-node-id parent-id})

(defn- create-rotation-outcome [path parent-id node-id->node-data]
  (let [parent-node (get node-id->node-data parent-id)
        outcome-type (node-conversion/decision-node->outcome-node-type (:type parent-node))]
    [::be-create-node (merge {:type outcome-type
                              :parent-id parent-id
                              :visualization {:ordinal (:ordinal path)}}
                             (select-keys path [:label :config]))]))

(defn- unlink-other-root-children [editor root-node-id rotation-node-id]
  (letfn [(unlink [nodes id]
            (assoc-in nodes [id :parent-id] nil))]
    (reduce
     (fn [editor [id {:keys [parent-id] :as node}]]
       (if (and (not= id rotation-node-id)
                (= parent-id root-node-id)
                (not (rotation/weighted-node? node)))
         (-> editor
             (update :node-id->node-data unlink id)
             (update :node-id->be-node unlink id))
         editor))
     editor
     (:node-id->node-data editor))))

(defn- rotation-node-id-from-step [{:keys [decision-node-id]} new-node]
  (or decision-node-id
      (when (rotation/weighted-node? new-node) (:id new-node))
      (:parent-id new-node)))

(defn- finish-rotation-setup [editor rotation-node-id]
  (-> editor
      (dissoc :rotation-setup)
      (set-selection [{:type :selection/decision-node
                       :decision-node-id rotation-node-id}])
      (assoc :new-node-selected true)))

(defn- continue-rotation-path-creation [db rotation-node-id remaining-paths]
  (if-let [[path & more-paths] (seq remaining-paths)]
    (let [node-id->node-data (get-in db [:decision-tree-editor :node-id->node-data])]
      {:db (assoc-in db [:decision-tree-editor :rotation-setup]
                     {:decision-node-id rotation-node-id
                      :remaining-paths more-paths})
       :dispatch (create-rotation-outcome path rotation-node-id node-id->node-data)})
    {:db (assoc db :decision-tree-editor
                (finish-rotation-setup (get-in db [:decision-tree-editor])
                                       rotation-node-id))
     :dispatch-n [[::toggle-auto-layout true]
                  [::sync-root-weighted-distribution]]}))

(defn- start-weighted-rotation-setup [db]
  {:db (assoc-in db [:decision-tree-editor :rotation-setup]
                 {:remaining-paths (rotation/initial-paths)})
   :dispatch [::be-create-node {:type rotation/weighted-node-type
                                :label "Ad Variant Rotation"}]})

(defn- resume-weighted-rotation [db root-node-id existing node-id->node-data]
  (let [rotation-node-id (:id existing)
        remaining-paths (rotation/remaining-initial-paths existing)
        linked? (rotation/weighted-node-linked-to-root? root-node-id existing)
        db (assoc-in db [:decision-tree-editor :rotation-setup]
                     {:decision-node-id rotation-node-id
                      :remaining-paths remaining-paths})]
    (cond
      (not linked?)
      {:db db
       :dispatch [::be-patch-node rotation-node-id {:parent-id root-node-id}
                  [::rotation-continue-after-link rotation-node-id remaining-paths]]}

      (seq remaining-paths)
      {:db db
       :dispatch (create-rotation-outcome (first remaining-paths)
                                          rotation-node-id
                                          node-id->node-data)}

      :else
      {:db db
       :dispatch-n [[::set-selection [{:type :selection/decision-node
                                       :decision-node-id rotation-node-id}]]
                    [::toggle-auto-layout true]]})))

(rf/reg-event-fx ::clear-root-even-rotation
  (fn [{:keys [db]} [_ on-complete]]
    (let [root-node-id (get-in db [:decision-tree-editor :root-node-id])
          config (get-in db [:decision-tree-editor :node-id->node-data root-node-id :config])]
      {:dispatch [::be-patch-node root-node-id
                  {:config (rotation/clear-root-distribution-config config)}
                  on-complete]})))

;; Applying a non-weighted strategy on the root removes any weighted rotation node,
;; since weights are only meaningful together with its paths.
(defn- set-root-distribution [db strategy & {:keys [close-ocp?]}]
  (let [root-node-id (get-in db [:decision-tree-editor :root-node-id])
        config (get-in db [:decision-tree-editor :node-id->node-data root-node-id :config])
        node-id->node-data (get-in db [:decision-tree-editor :node-id->node-data])
        weighted-ids (rotation/weighted-rotation-node-ids node-id->node-data)]
    {:dispatch-n (cond-> []
                   close-ocp? (conj [:ocp/close])
                   true (conj [::be-patch-node root-node-id
                               {:config (rotation/apply-distribution config strategy)}])
                   (seq weighted-ids) (conj [::delete-nodes {:node-ids weighted-ids
                                                             :clear-selection false}]))}))

(rf/reg-event-fx ::prepare-aco-rotation
  (fn [{:keys [db]} _]
    (set-root-distribution db "optimised")))

(rf/reg-event-fx ::set-even-rotation
  (fn [{:keys [db]} _]
    (set-root-distribution db "even" :close-ocp? true)))

(rf/reg-event-fx ::sync-root-weighted-distribution
  (fn [{:keys [db]} [_ on-complete]]
    (let [root-node-id (get-in db [:decision-tree-editor :root-node-id])
          node-id->node-data (get-in db [:decision-tree-editor :node-id->node-data])
          root-node-data (get node-id->node-data root-node-id)
          rotation-node (rotation/find-root-weighted-rotation-node root-node-id node-id->node-data)]
      (if-let [patch-data (when rotation-node
                            (rotation/root-weighted-distribution-patch
                             root-node-data rotation-node node-id->node-data))]
        {:dispatch [::be-patch-node root-node-id patch-data on-complete]}
        (when on-complete
          {:dispatch [:promise/resolve #(effects.util/invoke-callback on-complete)]})))))

(rf/reg-event-fx ::setup-rotation
  (fn [{:keys [db]} [_ mode]]
    (if (= (keyword mode) :even)
      {:dispatch [::set-even-rotation]}
      (let [editor (:decision-tree-editor db)
            root-node-id (:root-node-id editor)
            node-id->node-data (:node-id->node-data editor)
            existing (rotation/find-weighted-rotation-node root-node-id node-id->node-data)]
        (cond
          (and existing (rotation/setup-complete? existing)
               (rotation/weighted-node-linked-to-root? root-node-id existing))
          {:dispatch-n [[:ocp/close]
                        [::clear-root-even-rotation]
                        [::set-selection [{:type :selection/decision-node
                                           :decision-node-id (:id existing)}]]
                        [::toggle-auto-layout true]]}

          existing
          {:dispatch-n [[:ocp/close]
                        [::clear-root-even-rotation
                         [::resume-weighted-rotation-after-clear root-node-id (:id existing)]]]}

          :else
          {:dispatch-n [[:ocp/close]
                        [::clear-root-even-rotation
                         [::start-weighted-rotation-after-clear]]]})))))

(rf/reg-event-fx ::start-weighted-rotation-after-clear
  (fn [{:keys [db]} _]
    (start-weighted-rotation-setup db)))

(rf/reg-event-fx ::resume-weighted-rotation-after-clear
  (fn [{:keys [db]} [_ root-node-id rotation-node-id]]
    (let [node-id->node-data (get-in db [:decision-tree-editor :node-id->node-data])
          existing (get node-id->node-data rotation-node-id)]
      (resume-weighted-rotation db root-node-id existing node-id->node-data))))

(rf/reg-event-fx ::rotation-continue-after-link
  (fn [{:keys [db]} [_ rotation-node-id remaining-paths]]
    (let [root-node-id (get-in db [:decision-tree-editor :root-node-id])
          db (update db :decision-tree-editor
                     (fn [editor]
                       (-> editor
                           (unlink-other-root-children root-node-id rotation-node-id)
                           node-conversion/rebuild-reactflow-edges)))]
      (continue-rotation-path-creation db rotation-node-id remaining-paths))))

(defn- register-created-node [editor new-node]
  (let [{:keys [id type]} new-node
        selected-items [(if (node-conversion/outcome-type? type)
                          (get-outcome-selection-data new-node)
                          {:type :selection/decision-node
                           :decision-node-id id})]]
    (-> editor
        (node-conversion/update-fe-nodes-from-be-nodes [new-node])
        (set-selection selected-items)
        (assoc :new-node-selected true))))

(rf/reg-event-fx ::rotation-create-step
  (fn [{:keys [db]} [_ response]]
    (let [new-node (node-conversion/qualify-be-node-type (:data response))
          {:keys [parent-id id]} new-node
          setup (get-in db [:decision-tree-editor :rotation-setup])
          root-node-id (get-in db [:decision-tree-editor :root-node-id])
          editor (-> (:decision-tree-editor db)
                     (node-conversion/update-fe-nodes-from-be-nodes [new-node]))
          {:keys [remaining-paths]} setup
          rotation-node-id (rotation-node-id-from-step setup new-node)
          db (assoc db :decision-tree-editor editor)]
      (cond
        (and (rotation/weighted-node? new-node)
             (nil? parent-id)
             root-node-id)
        {:db (assoc-in db [:decision-tree-editor :rotation-setup]
                       (assoc setup :decision-node-id rotation-node-id))
         :dispatch [::be-patch-node id {:parent-id root-node-id}
                    [::rotation-continue-after-link rotation-node-id remaining-paths]]}

        (seq remaining-paths)
        (continue-rotation-path-creation db rotation-node-id remaining-paths)

        :else
        {:db (assoc db :decision-tree-editor
                    (finish-rotation-setup editor rotation-node-id))
         :dispatch-n [[::toggle-auto-layout true]
                      [::sync-root-weighted-distribution]]}))))

(rf/reg-event-fx ::be-create-node-on-success
  (fn [{:keys [db]} [_ response]]
    (if (get-in db [:decision-tree-editor :rotation-setup])
      {:dispatch [::rotation-create-step response]}
      (let [new-node (node-conversion/qualify-be-node-type (:data response))]
        {:db (assoc db :decision-tree-editor
                    (register-created-node (:decision-tree-editor db) new-node))}))))

;; Handled by the generic error handler in track-mutation
(rf/reg-event-db ::be-create-node-on-failure
  identity)

(rf/reg-event-fx ::be-duplicate-node
  [(rf/inject-cofx :sub :url-context)
   (rf/inject-cofx :sub :resource.user/token)]
  (fn [{:keys [db url-context resource.user/token]}
       [_ {:keys [id include-subtree select-after]}]]
    (let [decision-tree-id (get-in db [:decision-tree-editor :decision-tree-id])]
      (-> {:db db}
          (track-mutation {:method :post
                           :uri (url-tpl/render :api/content-build-adset-decision-tree-node-duplicate
                                                (-> url-context
                                                    (assoc :decision-tree-id decision-tree-id
                                                           :node-id id)))
                           :headers {"Authorization" token}
                           :params {:times 1
                                    :include-subtree (boolean include-subtree)}
                           :on-success [::be-duplicate-node-on-success select-after]
                           :on-failure [::be-duplicate-node-on-failure]})))))

(rf/reg-event-fx ::be-duplicate-node-on-success
  (fn [{:keys [db]} [_ select-after response]]
    (let [new-be-nodes (-> response ->clj :data :nodes)]
      (-> {:db db}
          (mr/update-db update :decision-tree-editor
                        (fn [editor]
                          (-> editor
                              (node-conversion/update-fe-nodes-from-be-nodes new-be-nodes)
                              (set-selection
                               (case select-after
                                 :first-outcome
                                 (some (fn [{:keys [type] :as new-node}]
                                         (when (node-conversion/outcome-type? type)
                                           [(get-outcome-selection-data new-node)]))
                                       new-be-nodes)

                                 :decision-nodes
                                 (keep (fn [{:keys [type id]}]
                                         (when-not (node-conversion/outcome-type? type)
                                           {:type :selection/decision-node
                                            :decision-node-id id}))
                                       new-be-nodes))))))))))

;; Handled by the generic error handler in track-mutation
(rf/reg-event-db ::be-duplicate-node-on-failure
  identity)

(rf/reg-event-fx ::be-refresh-node
  [(rf/inject-cofx :sub :url-context)
   (rf/inject-cofx :sub :resource.user/token)]
  (fn [{:keys [db url-context resource.user/token]}
       [_ {:keys [id]}]]
    (let [decision-tree-id (get-in db [:decision-tree-editor :decision-tree-id])]
      (-> {:db db}
          (track-mutation {:method :post
                           :uri (url-tpl/render :api/content-build-adset-decision-tree-node-refresh
                                                (assoc url-context
                                                       :decision-tree-id decision-tree-id
                                                       :node-id id))
                           :headers {"Authorization" token}
                           :on-success [::be-refresh-node-on-success]
                           :on-failure [::be-refresh-node-on-failure]})))))

(rf/reg-event-fx ::be-refresh-node-on-success
  [(rf/inject-cofx :sub :url-context)]
  (fn [{:keys [db url-context]} [_ response]]
    (let [{:keys [advertiser-id adset-id]} url-context
          decision-tree-id (get-in db [:decision-tree-editor :decision-tree-id])
          nodes-added (get-in response [:data :nodes-added])]
      (cond-> {:db db}
        (not= 0 nodes-added)
        (mr/conj-fx [:invalidate-queries
                     {:queryKey ["decision-tree" advertiser-id adset-id decision-tree-id]}])))))

;; Handled by the generic error handler in track-mutation
(rf/reg-event-db ::be-refresh-node-on-failure
  identity)

(rf/reg-event-db ::set-selection
  [(rf/path :decision-tree-editor)]
  (fn [editor [_ selected-entities]]
    (set-selection editor selected-entities)))

(rf/reg-event-db ::select-nodes
  [(rf/path :decision-tree-editor)]
  (fn [editor [_ node-ids]]
    (set-selected-nodes editor node-ids)))

(rf/reg-event-fx ::handle-property-form-save
  (fn [{:keys [db]} [_ {:keys [node-id patch-key patch-data]}]]
    (-> {:db db}
        (mr/update-db assoc-in
                      (conj [:decision-tree-editor :node-id->node-data node-id] patch-key)
                      patch-data)
        (be-patch-nodes [node-id] 1000))))

(rf/reg-event-fx ::add-node-outcome
  (fn [{:keys [db]} [_ {decision-node-id :id node-type :type}]]
    (let [node-id->node-data (-> db :decision-tree-editor :node-id->node-data)
          outcome-node-ids (-> decision-node-id node-id->node-data :outcome-node-ids)
          new-node-ordinal (-> outcome-node-ids
                               (->> (keep (fn [outcome-node-id]
                                            (-> outcome-node-id node-id->node-data :ordinal)))
                                    (reduce max 0))
                               inc)
          outcome-type (node-conversion/decision-node->outcome-node-type node-type)
          {:keys [label]} (node-conversion/->outcome-node node-type)
          rotation? (rotation/variants-rotation? {:type node-type})
          path-label (when rotation?
                       (or (rotation/next-path-label outcome-node-ids node-id->node-data)
                           (str "Path " (inc (count outcome-node-ids)))))]
      {:dispatch [::be-create-node (cond-> {:type outcome-type
                                            :label (or path-label label)
                                            :visualization {:ordinal new-node-ordinal}
                                            :parent-id decision-node-id}
                                     rotation?
                                     (assoc :config rotation/default-path-config))]})))

(rf/reg-event-db ::assoc-reactflow-edge
  (fn [db [_ edge-id k v]]
    (update-in db [:decision-tree-editor :reactflow-edges edge-id]
               (fn [^js edge] (assoc-reactflow-object edge k v)))))

(rf/reg-event-fx ::update-sub-path-visibility
  (fn [{:keys [db]} [_ {:keys [edge-id is-visible]}]]
    (-> {:db db}
        (mr/update-db update :decision-tree-editor
                      update-sub-path-visibility edge-id is-visible))))

(defn bulk-update-nodes [effects params {:keys [url-context token on-success on-failure]}]
  (-> effects
      (track-mutation {:method :post
                       :uri (url-tpl/render :api/content-build-adset-decision-tree-bulk-update url-context)
                       :headers {"Authorization" token}
                       :params params
                       :on-success on-success
                       :on-failure on-failure})))

(defn apply-bulk-patch-data-on-be-nodes [editor bulk-patch-data]
  (update editor :node-id->be-node
          (fn [node-id->be-node]
            (reduce
             (fn [node-id->be-node {:keys [id] :as patch-data}]
               (update node-id->be-node id merge (dissoc patch-data :id)))
             node-id->be-node
             bulk-patch-data))))

(rf/reg-event-fx ::reorder-outcome-node
  [(rf/inject-cofx :sub :url-context)
   (rf/inject-cofx :sub :resource.user/token)]
  (fn [{:keys [db url-context resource.user/token]} [_ decision-node-id active-outcome-node-id over-outcome-node-id]]
    (let [{decision-tree-id :decision-tree-id
           original-node-id->node-data :node-id->node-data} (:decision-tree-editor db)
          outcome-node-ids (-> decision-node-id original-node-id->node-data :outcome-node-ids)
          id->index (into {} (map-indexed (fn [index id] [id index])) outcome-node-ids)
          active-index (-> active-outcome-node-id id->index)
          over-index (-> over-outcome-node-id id->index)

          ;; Swap the node ids
          new-outcome-node-ids (->> outcome-node-ids
                                    (medley/remove-nth active-index)
                                    (medley/insert-nth over-index active-outcome-node-id)
                                    vec)

          ;; Update the ordinal value in each affected outcome-node
          node-id->node-data (reduce (fn [node-id->node-data [index node-id]]
                                       (assoc-in node-id->node-data [node-id :ordinal] index))
                                     original-node-id->node-data
                                     (mc/seq-indexed new-outcome-node-ids))

          ;; Update the ordered list of outcome-node-ids
          node-id->node-data (-> node-id->node-data
                                 (assoc-in [decision-node-id :outcome-node-ids] new-outcome-node-ids))
          bulk-patch-data (->> new-outcome-node-ids
                               (mapv (fn [node-id]
                                       (let [ordinal (-> node-id node-id->node-data :ordinal)]
                                         {:id node-id
                                          :visualization {:ordinal ordinal}}))))]
      (-> {:db db}
          (mr/update-db assoc-in [:decision-tree-editor :node-id->node-data] node-id->node-data)
          (bulk-update-nodes bulk-patch-data
                             {:url-context (-> url-context
                                               (assoc :decision-tree-id decision-tree-id))
                              :token token
                              :on-success [::reorder-outcome-node-on-success bulk-patch-data]
                              :on-failure [::reorder-outcome-node-on-failure original-node-id->node-data]})))))

(rf/reg-event-fx ::reorder-outcome-node-on-success
  (fn [{:keys [db]} [_ bulk-patch-data _response]]
    (-> {:db db}
        (mr/update-db update :decision-tree-editor
                      apply-bulk-patch-data-on-be-nodes bulk-patch-data))))

(rf/reg-event-fx ::reorder-outcome-node-on-failure
  (fn [{:keys [db]} [_ original-node-id->node-data _response]]
    ;; Modal handled by the generic error handler in track-mutation
    (-> {:db db}
        (mr/update-db assoc-in [:decision-tree-editor :node-id->node-data]
                      original-node-id->node-data))))

(rf/reg-event-fx ::re-layout-nodes
  [(rf/inject-cofx :sub :url-context)
   (rf/inject-cofx :sub :resource.user/token)]
  (fn [{:keys [db url-context resource.user/token]} _]
    (let [decision-tree-id (-> db :decision-tree-editor :decision-tree-id)
          {:keys [reactflow-nodes node-ids-to-patch]} (layout/re-layout-nodes (-> db :decision-tree-editor))
          bulk-patch-data (->> node-ids-to-patch
                               (mapv (fn [node-id]
                                       (let [position (-> node-id
                                                          reactflow-nodes
                                                          ->clj
                                                          :position)]
                                         {:id node-id
                                          :visualization position}))))]
      (-> {:db db}
          ;; Bulk-patch all the affected nodes immediately
          (bulk-update-nodes bulk-patch-data
                             {:url-context (-> url-context
                                               (assoc :decision-tree-id decision-tree-id))
                              :token token
                              :on-success [::re-layout-nodes-on-success bulk-patch-data reactflow-nodes]
                              :on-failure [::re-layout-nodes-on-failure]})))))

(rf/reg-event-db ::re-layout-nodes-on-success
  [(rf/path :decision-tree-editor)]
  (fn [editor [_ bulk-patch-data reactflow-nodes _response]]
    (-> editor
        (apply-bulk-patch-data-on-be-nodes bulk-patch-data)
        (assoc :reactflow-nodes reactflow-nodes))))

;; Handled by the generic error handler in event/track-mutation
(rf/reg-event-fx ::re-layout-nodes-on-failure
  (fn [_ [_ _response]]))

(rf/reg-event-fx ::toggle-auto-layout
  (fn [{:keys [db]} [_ value]]
    (-> {:db db}
        (mr/update-db update-in [:decision-tree-editor :auto-layout]
                      #(if value value (not %))))))

(rf/reg-event-fx ::update-ad-variant-search
  (fn [{{{:keys [node-id->node-data]} :decision-tree-editor :as db} :db}
       [_ input-text fit-view]]
    (let [lower-cased-input (str/lower-case input-text)
          selected-items
          (keep (fn [[_ {:keys [ad-variants label targeting] :as node-data}]]
                  (when (or (str/includes? (str/lower-case label)
                                           lower-cased-input)
                            (some #(str/includes? (str/lower-case (:content-id %))
                                                  lower-cased-input)
                                  ad-variants)
                            (when-let [value (predicate-converter/predicates->string-value (:predicate targeting))]
                              (str/includes? (str/lower-case value)
                                             lower-cased-input)))
                    (-> node-data
                        (update :category
                                #(if (= % :decision-node) :default-outcome-node %))
                        selection/get-selected-item-for-node)))
                node-id->node-data)]
      {:db (update db :decision-tree-editor
                   (fn [editor]
                     (cond-> editor
                       (not (str/blank? input-text)) (set-selection selected-items)
                       true (assoc :ad-variant-search {:text input-text
                                                       :enabled true}))))
       :promise/resolve #(fit-view
                          (set (map (comp str :decision-node-id) selected-items)))})))

(rf/reg-event-db ::enable-ad-variant-search
  [(rf/path :decision-tree-editor)]
  (fn [editor [_ value]]
    (assoc-in editor [:ad-variant-search :enabled] (boolean value))))

(rf/reg-event-fx ::bulk-apply-trackers
  [(rf/inject-cofx :sub :url-context)
   (rf/inject-cofx :sub :resource.user/token)]
  (fn [{:keys [db url-context resource.user/token]} [_ bulk-patch-data]]
    (let [decision-tree-id (-> db :decision-tree-editor :decision-tree-id)]
      (-> {:db db}
          (bulk-update-nodes bulk-patch-data
                             {:url-context (-> url-context
                                               (assoc :decision-tree-id decision-tree-id))
                              :token token
                              :on-success [::bulk-apply-trackers-on-success]
                              :on-failure [::bulk-apply-trackers-on-failure]})))))

(defn get-decision-tree-toaster-dispatch [message-details]
  [:toaster-bus/add
   (merge {:id :decision-tree-toaster
           :type :success
           :snackbar-props {:anchorOrigin #js {:vertical "bottom"
                                               :horizontal "left"}
                            :sx #js {:ml 5
                                     :minWidth "60rem"}}}
          message-details)])

(rf/reg-event-fx ::bulk-apply-trackers-on-success
  [(rf/inject-cofx :sub :url-context)]
  (fn [{{:keys [advertiser-id adset-id]} :url-context
        {{:keys [decision-tree-id]} :decision-tree-editor} :db}]
    {:fx [[:invalidate-queries
           {:queryKey ["decision-tree" advertiser-id adset-id decision-tree-id]}]
          [:dispatch [:ocp/close]]
          [:dispatch (get-decision-tree-toaster-dispatch
                      {:message "Successfully saved Tracker Values"})]]}))

(rf/reg-event-fx ::bulk-apply-trackers-on-failure
  identity)
